import ChatDisclaimer from "@/components/chat/ChatDisclaimer";
import type { SessionResponse } from "@/services/aiService";

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  });
}

function truncateTitle(title: string | null, fallback: string): string {
  const text = title || fallback;
  return text.length > 28 ? text.slice(0, 25) + "..." : text;
}

interface Props {
  sessions: SessionResponse[];
  activeSessionId: string | null;
  viewingHistorySessionId: string | null;
  onSelectSession: (sessionId: string) => void;
  onReturnToLive: () => void;
}

export default function ChatHistory({
  sessions,
  activeSessionId,
  viewingHistorySessionId,
  onSelectSession,
  onReturnToLive,
}: Props) {
  const isViewingHistory = viewingHistorySessionId !== null;

  return (
    <div className="flex flex-col w-full sm:w-[var(--chat-history-width)] border-r-0 sm:border-r border-[var(--chat-grey-100)] px-[16px] py-[var(--chat-space-06)] sm:px-[var(--chat-space-06)] sm:pl-[40px] shrink-0 flex-1 sm:flex-none overflow-hidden">
      {/* Header */}
      <h3 className="font-['Inter'] text-[12px] font-semibold leading-[1.2] tracking-[0.6px] uppercase text-[var(--color-greyscale-black,#11161A)] mb-[var(--chat-space-07)]">
        Conversation History
      </h3>

      {/* Return to live button */}
      {isViewingHistory && (
        <button
          onClick={onReturnToLive}
          className="mb-[var(--chat-space-04)] text-left text-[14px] leading-[1.5] text-[var(--chat-pine)] font-medium hover:opacity-70 transition-opacity"
        >
          &larr; Back to current chat
        </button>
      )}

      {/* Session list */}
      <div className="flex flex-col gap-[var(--chat-space-05)] flex-1 overflow-y-auto">
        {sessions.length === 0 && (
          <p className="text-[16px] text-[var(--chat-grey-500)] leading-[1.5]">
            No past conversations yet.
          </p>
        )}
        {sessions.map((session) => {
          const isActive =
            viewingHistorySessionId === session.session_id ||
            (!viewingHistorySessionId &&
              activeSessionId === session.session_id);

          return (
            <button
              key={session.session_id}
              onClick={() => {
                if (session.session_id === activeSessionId) {
                  if (isViewingHistory) onReturnToLive();
                  return;
                }
                onSelectSession(session.session_id);
              }}
              className="flex items-center gap-[var(--chat-space-04)] text-left text-[16px] leading-[1.4] hover:opacity-70 transition-opacity"
            >
              {isActive && (
                <span className="w-[8px] h-[8px] rounded-full bg-[var(--chat-black)] shrink-0" />
              )}
              <span className="text-[var(--chat-grey-500)] shrink-0 font-normal">
                {formatDate(session.created_at)}
              </span>
              <span
                className={
                  isActive
                    ? "text-[var(--chat-black)] font-semibold truncate"
                    : "text-[var(--chat-black)] font-medium truncate"
                }
              >
                {truncateTitle(session.title, "New conversation")}
              </span>
            </button>
          );
        })}
      </div>

      {/* Disclaimer — mobile only (desktop shows it in the main chat area) */}
      <div className="sm:hidden shrink-0">
        <ChatDisclaimer />
      </div>
    </div>
  );
}
